import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  Signal,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import {
  NavigationService,
  StatusLabelComponent,
  StatusLabelTag,
  TableColumn,
  TableComponent,
  TableOptions,
  ToastService,
} from '@h5s/design-system';
import { Customer, PsCustomer, PublicSecurityStatus } from '@h5s/entities';
import { SaveProfileRegistryEvent } from '@h5s/feature-profile-registry';
import { MenuItem } from '@h5s/util-button';
import { EventBusService } from '@h5s/util-event';
import { TranslationService } from '@h5s/util-translate';
import { catchError, filter, first, map, startWith, switchMap, tap, throwError } from 'rxjs';
import { FeaturePsService } from '../../../feature-ps.service';
import { FEATURE_PS_TRANSLATIONS } from '../../translations/feature-ps.translations';
import { PsFilterModel } from '../ps-filter/models/ps-filters-model';
import { TablePsCustomer } from './models/table-ps-customer';
import { TooltipValidationError } from './models/tooltip-validation-error';

const DESIGN_SYSTEM = [TableComponent, StatusLabelComponent];

/**
 * Eventi gestiti:
 *
 * SaveProfileRegistryEvent
 */
@Component({
  selector: 'h5s-ps-table',
  imports: [ReactiveFormsModule, ...DESIGN_SYSTEM],
  templateUrl: './ps-table.component.html',
  styleUrl: './ps-table.component.scss',
})
export class PsTableComponent implements OnInit {
  private readonly featurePsService = inject(FeaturePsService);
  private readonly navigationService = inject(NavigationService);
  private readonly toastService = inject(ToastService);
  protected readonly translationService = inject(TranslationService);
  private readonly eventBusService = inject(EventBusService);
  private readonly destroyRef = inject(DestroyRef);

  /** Traduzioni */
  public readonly translation = FEATURE_PS_TRANSLATIONS;

  /** Template */
  readonly tooltipPSCompleteStatus = viewChild<TemplateRef<unknown>>('tooltipPSCompleteStatus');
  readonly tooltipPSSentError = viewChild<TemplateRef<unknown>>('tooltipPSSentError');
  readonly psCompleteStatus = viewChild<TemplateRef<unknown>>('psCompleteStatus');
  readonly psStateStatus = viewChild<TemplateRef<unknown>>('psStateStatus');

  selectedRecords = this.featurePsService.selectedTablePsCustomers;

  readonly publicSecurityStatus = PublicSecurityStatus;

  readonly loading = signal<boolean>(false);
  isLoadingReservationId = false;

  public readonly filters = input<PsFilterModel | null>(null);

  public readonly tablePsCustomers: Signal<TablePsCustomer[]> = toSignal(
    toObservable(this.filters).pipe(
      filter((filters): filters is PsFilterModel => !!filters),
      switchMap((filters) => {
        return this.featurePsService.research.pipe(
          map(() => filters),
          startWith(filters),
        );
      }),
      tap(() => {
        this.loading.set(true);
      }),
      switchMap((filters) => this.featurePsService.getFilteredPsCustomers(filters.date, filters.profile)),
      map((customers: PsCustomer[]) => customers.map((customer) => new TablePsCustomer(customer))),
      tap(() => {
        this.loading.set(false);
      }),
      catchError((error) => {
        this.loading.set(false);
        this.toastService.showError(this.translation.errorLoadData);
        return throwError(() => error);
      }),
    ),
    { initialValue: [] },
  );

  //-- Tabella ---------------------------------------

  /** Colonne della tabella */
  public readonly tableColumns = computed<TableColumn<TablePsCustomer>[]>(() => {
    const columns: TableColumn<TablePsCustomer>[] = [
      {
        key: 'isValid',
        header: this.translation.completeHeader,
        showAs: 'template',
        customTemplate: this.psCompleteStatus(),
      },
    ];

    if (this.showState()) {
      columns.push({
        key: 'publicSecurityStatus',
        header: this.translation.stateHeader,
        customTemplate: this.psStateStatus(),
      });
    }

    columns.push(
      {
        key: 'name',
        header: this.translation.nameHeader,
        getCellDisplayValue: (r: TablePsCustomer): string => r.getDisplayName(),
      },
      {
        key: 'relatives',
        header: this.translation.groupMemberHeader,
        getCellDisplayValue: (r: TablePsCustomer): string =>
          r.relatives.map((customer) => customer.getDisplayName()).join(', '),
      },
      {
        key: 'guestsNumber',
        header: this.translation.guestsNumberHeader,
        getCellDisplayValue: (r: TablePsCustomer): number => r.guestsNumber,
        showTotal: true,
      },
      {
        key: 'room',
        header: this.translation.roomHeader,
        getCellDisplayValue: (r: TablePsCustomer): string => r.room,
      },
      {
        key: 'customerData',
        header: this.translation.noteHeader,
        getCellDisplayValue: (r: TablePsCustomer): string => r.customerData.notes ?? '',
      },
    );

    return columns;
  });

  /** Opzioni tabella */
  public readonly tableOptions = signal<TableOptions<TablePsCustomer>>({
    isEditable: false,
    enableSelection: true,
    selectionMode: 'multiple',
    enablePagination: false,
    showActionOnHover: true,
    rowCheckboxDisabled: (row) => !row.isValid,
    disableRowClick: (row) => !row.isValid,
    secondaryRowActions: [
      {
        description: this.translation.rowActionCustomerDetail,
        icon: 'uil uil-user',
        action: (row): void => {
          if (row) this.navigationService.changeTab(`/customer/edit/${row.id}`);
        },
      },
      {
        description: this.translation.rowActionFamilyMember,
        icon: 'uil uil-users-alt',
        isHidden: (row): boolean => (row ? row.relatives.length === 0 : true),
        getSubactions: (row): MenuItem<TablePsCustomer | undefined>[] => {
          return (
            row?.relatives.map((customer) => ({
              description: customer.getDisplayName(),
              icon: 'uil uil-user',
              action: (): void => {
                this.navigationService.changeTab(`/customer/edit/${customer.id}`);
              },
            })) ?? []
          );
        },
      },
      {
        description: this.translation.rowActionReservationDetail,
        icon: 'uil uil-bag',
        action: (row): void => {
          const purchasedStayId = row?.purchasedStayToCustomer.purchasedStayId;
          if (this.isLoadingReservationId) return;

          if (row && purchasedStayId !== undefined && purchasedStayId !== null) {
            this.isLoadingReservationId = true;
            this.featurePsService
              .getReservationIdByPurchasedStay(purchasedStayId)
              .pipe(first())
              .subscribe({
                next: (id) => {
                  this.navigationService.changeTab(`/reservation/edit/${id}`);
                },
                error: () => {
                  this.isLoadingReservationId = false;
                },
                complete: () => {
                  this.isLoadingReservationId = false;
                },
              });
          }
        },
      },
    ],
  });

  readonly showState = toSignal(this.featurePsService.showStatus, { initialValue: false });

  constructor() {
    effect(() => {
      if (this.tablePsCustomers()) this.setSelectedRecordsComplete();
    });

    this.featurePsService.selectReadyPsCustomer.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.setSelectedRecordsComplete();
    });
  }

  public ngOnInit(): void {
    this.eventBusService
      .ofFiltered(SaveProfileRegistryEvent, (payload: SaveProfileRegistryEvent) =>
        this.tablePsCustomers().some((r) => r.id === payload.profileRegistryId),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.featurePsService.research.next();
      });
  }

  public setSelectedRecordsComplete(): void {
    if (!this.tablePsCustomers()) return;
    this.selectedRecords.set((this.tablePsCustomers() ?? []).filter((customer) => customer.isValid));
  }

  public getCustomerErrors(id: number): TooltipValidationError[] {
    const psCustomers = this.tablePsCustomers();
    if (!psCustomers) return [];

    const customer = psCustomers.find((c) => c.id === id);
    if (!customer) return [];

    // Crea un array con il cliente e i suoi parenti
    const relatedCustomers = [
      new Customer({
        id: customer.id,
        surname: customer.surname,
        name: customer.name,
      }),
      ...customer.relatives,
    ];

    return (
      customer.validationResults?.map(({ resourceId, validationErrors }) => {
        const matchedCustomer = relatedCustomers.find((c) => c.id === resourceId);
        return {
          id: resourceId,
          name: matchedCustomer?.getDisplayName() ?? '',
          errors: validationErrors?.map((err) => err.translationKey),
        };
      }) ?? []
    );
  }

  /**
   * Ottiene lo stato della PS da visualizzare nella label
   *
   * @param {PublicSecurityStatus} publicSecurityStatus
   * @returns {StatusLabelTag}
   */
  public getStatusByPublicSecurityStatus(publicSecurityStatus: PublicSecurityStatus): StatusLabelTag {
    switch (publicSecurityStatus) {
      case PublicSecurityStatus.NotSent:
        return 'light-dark';

      case PublicSecurityStatus.SentError:
        return 'danger';

      case PublicSecurityStatus.SentOk:
        return 'confirm';
    }
  }

  /**
   * Ottiene il testo da visualizzare nella label dello stato della PS
   *
   * @param {PublicSecurityStatus} publicSecurityStatus
   * @returns {string}
   */
  public getTextByPublicSecurityStatus(publicSecurityStatus: PublicSecurityStatus): string {
    switch (publicSecurityStatus) {
      case PublicSecurityStatus.NotSent:
        return this.translation.publicSecurityStatusNotSent;

      case PublicSecurityStatus.SentError:
        return this.translation.publicSecurityStatusSentError;

      case PublicSecurityStatus.SentOk:
        return this.translation.publicSecurityStatusSentOk;
    }
  }
}
